import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  CustomTooltipTracker,
  TrendChartSummarySection,
} from "../../../HealthChart/components";
import {
  useChartSelection,
  useTrendChartCompactViewport,
} from "../../../HealthChart/hooks";
import {
  calculateDynamicYDomain,
  getBmiTrendChartLayout,
  getChartFocusStyles,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

export type BMIChartDataPoint = ChartDataPoint & {
  bmi: number;
};

export interface BMIChartProps {
  data: BMIChartDataPoint[];
}

import {
  getBmiColor,
  formatBmiDisplay,
  ZONE_LABELS,
  getZoneLabelColor,
} from "../../../HealthChart/utils/bmiChartHelpers";
export { getBmiColor };

const CHART_HEIGHT = 200;
const Y_MARGIN = { top: 10, bottom: 10 };
const CHART_BOTTOM_MARGIN = 20;
const DEFAULT_Y_DOMAIN: [number, number] = [10, 40];
const Y_TICKS = [16, 25, 30];

const BMIChart: React.FC<BMIChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const {
    left: chartLeftMargin,
    right: chartRightMargin,
    yAxisWidth,
  } = getBmiTrendChartLayout(isCompactChart);

  const yDomain = useMemo(
    () =>
      calculateDynamicYDomain(
        data.map((d) => d.bmi),
        DEFAULT_Y_DOMAIN
      ),
    [data]
  );
  const yRange = yDomain[1] - yDomain[0];

  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  if (!activePoint) {
    return null;
  }

  const activeBmi = (activePoint as BMIChartDataPoint).bmi ?? 0;
  const displayBmi = formatBmiDisplay(activeBmi);

  return (
    <>
      <style>{getChartFocusStyles("bmi-chart")}</style>
      <Box className="bmi-chart" sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          <Typography variant="h4" sx={{ fontSize: 16 }}>
            {t("HealthChart.healthHistoryChart.title")}
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: CHART_HEIGHT,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: chartLeftMargin,
                  bottom: CHART_BOTTOM_MARGIN,
                }}
                onClick={handlePointClick}
              >
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal
                />

                <ReferenceArea
                  y1={30}
                  y2={40}
                  fill={backgroundColors.errorLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={25}
                  y2={30}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={16}
                  y2={25}
                  fill={backgroundColors.successLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={10}
                  y2={16}
                  fill={backgroundColors.errorLight}
                  fillOpacity={0.5}
                />

                <ReferenceLine
                  y={30}
                  stroke={themeColors.error}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={25}
                  stroke={themeColors.warning}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={16}
                  stroke={themeColors.error}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />

                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={Y_TICKS}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={yAxisWidth}
                />

                <Tooltip
                  content={(props) => (
                    <CustomTooltipTracker
                      {...props}
                      setHoveredPoint={setHoveredPoint}
                    />
                  )}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                <Line
                  type="linear"
                  dataKey="bmi"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot {...props} getColorFn={getBmiColor} />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot {...props} getColorFn={getBmiColor} />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `calc(100% - ${chartRightMargin}px)`,
                width: chartRightMargin,
                boxSizing: "border-box",
                pr: 1,
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              {ZONE_LABELS.map(({ yMin, yMax, labelKey }) => {
                const plotHeight =
                  CHART_HEIGHT - Y_MARGIN.top - CHART_BOTTOM_MARGIN;
                const zoneCenterY = (yMin + yMax) / 2;
                const yToTop =
                  Y_MARGIN.top +
                  plotHeight * (1 - (zoneCenterY - yDomain[0]) / yRange);
                const color = getZoneLabelColor(labelKey);
                return (
                  <Box
                    key={labelKey}
                    sx={{
                      position: "absolute",
                      left: 6,
                      right: 0,
                      top: yToTop - 6,
                      height: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(labelKey)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={chartLeftMargin}
            chevronRightMargin={chartRightMargin}
            datePrefix={t("HealthChart.healthHistoryChart.datePrefix")}
            fullDate={activePoint.fullDate}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: getBmiColor(activeBmi),
                }}
              >
                {displayBmi}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.healthHistoryChart.unit")}
              </span>
            </div>
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default BMIChart;
